/**
 * Treasury coordinates — single source of truth for accepting funds.
 * Stripe and PayPal route through their own SDK keys (env-managed).
 * Solana payments route to the Director-locked celestial coordinate below.
 *
 * IMPORTANT: This address is RECEIVE-ONLY. The application never holds the
 * private key. All sends from this wallet must be performed by the Director
 * directly via Phantom/Solflare or the Vault wallet.
 */
export const TREASURY = {
    solana: {
        coordinate: "2Uq3hvn9eYa8y2jcxKuJ6XivV1MNq4Jm7XA9XgE1QMEx",
        label: "EIGOO Sovereign Treasury",
        // Mainnet USDC mint (Circle)
        usdc_mint: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
        cluster: "mainnet-beta",
    },
};

/**
 * Public brand identity. The Director confirmed EIGOO INC as the definitive
 * global brand on 2026.02 (reverting the brief BUSINESS BOOGIE rebrand).
 *
 * EIGOO is positioned as an "Elite Infrastructure Foundry" — the parent brand
 * across every customer-facing surface: headers, page titles, navbar wordmarks,
 * email signatures, marketing collateral, social cards, OG metadata.
 *
 * Slogan / acronym narrative: "Empower. Invest. Grow. Opportunity Online."
 *
 * `bizboogie.com` remains the operating domain; `BIZBOOGIE LLC` (in
 * LEGAL_ENTITY) remains the registered tax-and-banking entity.
 */
export const BRAND = {
    name: "EIGOO Inc.",
    name_upper: "EIGOO",
    long_name: "EIGOO Inc.",
    tagline: "Empower. Invest. Grow. Opportunity Online.",
    tagline_short: "The Everything App for Sovereignty",
    mission: "Elite Infrastructure Foundry · The Everything App for Sovereignty",
    legal_operator: "BIZBOOGIE LLC",
    domain: "bizboogie.com",
    // Acronym expansion (used in hero / about / footer flourish surfaces)
    acronym: [
        { letter: "E", verb: "Empower", clause: "the spirit." },
        { letter: "I", verb: "Invest", clause: "in the infrastructure." },
        { letter: "G", verb: "Grow", clause: "the legacy." },
        { letter: "OO", verb: "Opportunity", clause: "online." },
    ],

    // The Everything App for Sovereignty — six monetization sectors.
    // Each track is hot-wired to its corresponding Sovereign Node via
    // the `url` field; the SovereignSectors card renders an "ENGAGE"
    // CTA on every Track card that hops to that node.
    // Director directive 2026.06 · Strike #24.
    sectors: [
        {
            key: "finance",
            label: "Finance",
            short: "Trade · Capital",
            description: "Markets, treasury, stake delegation, signal intelligence.",
            accent: "hsl(153 60% 60%)",
            url: "https://tax.eigooinc.net",
        },
        {
            key: "security",
            label: "Security",
            short: "TechOps · Vault",
            description: "TechOps front-door + TechOpsSA Vault. Adversarial research, audits, hardening.",
            accent: "hsl(350 75% 65%)",
            url: "https://auth.eigooinc.net",
        },
        {
            key: "artisan",
            label: "Artisan",
            short: "Island Gold · Craft",
            description: "Heirloom-grade builds, bespoke jewelry, artisan goods. Sovereign craft economy.",
            accent: "hsl(45 85% 60%)",
            url: "https://islandgold.eigooinc.net",
        },
        {
            key: "lifestyle",
            label: "Lifestyle",
            short: "Hi · Sovereign Living",
            description: "Hawaii lifestyle, community, sovereign daily ops. The flagship operator hub.",
            accent: "hsl(25 90% 60%)",
            url: "https://hi.eigooinc.net",
        },
        {
            key: "music",
            label: "Music",
            short: "Vocal Command · Sound",
            description: "Music enterprise, podcasts, vocal command studios. Audio sovereignty.",
            accent: "hsl(280 70% 65%)",
            url: "https://music.eigooinc.net",
        },
        {
            key: "contest",
            label: "Contest",
            short: "Siren · Arena",
            description: "Tournaments, sovereign contests, leaderboard economies, prediction markets.",
            accent: "hsl(195 70% 65%)",
            url: "https://siren.eigooinc.net",
        },
    ],
};

/**
 * EIGOO directorate identity registry.
 * Single source of truth for the Director's signature, alias, signing copy,
 * and the legal entity coordinate. Imported anywhere a "signed by" block,
 * sales hub email, contact footer, or investor-relations banner is rendered.
 *
 * IMPORTANT: BIZBOOGIE LLC operates with TWO addresses by design:
 *   - OPERATIONAL ("mailing / business")  → Mililani, HI 96789
 *       Used on every PUBLIC surface: footer, contact page, SPF signature,
 *       partner-facing invoices, marketing collateral.
 *   - LEGAL / REGISTERED ("tax / IRS")    → Fayetteville, NC 28311
 *       Used STRICTLY on backend tax artifacts (W-9, 1099, IRS filings,
 *       bank-of-record verifications). Never surfaced on public UI.
 */

export const LEGAL_ENTITY = {
    name: "BIZBOOGIE LLC",
    parent_brand: "EIGOO Inc.",
    // ---- Operational / public-facing coordinate (HQ of record) -------
    address_line_1: "95-390 Kuahelani Avenue 3AC-1320",
    address_line_2: "Mililani, HI 96789",
    address_country: "USA",
    address_oneline:
        "95-390 Kuahelani Avenue 3AC-1320, Mililani, HI 96789, USA",
    address_block: [
        "BIZBOOGIE LLC",
        "95-390 Kuahelani Avenue 3AC-1320",
        "Mililani, HI 96789, USA",
    ],
};

/**
 * Registered legal address — IRS / tax-form use only.
 * DO NOT surface on the public UI. Imported only by backend tax templates,
 * W-9 generators, and Director-only ledger views.
 */
export const REGISTERED_ENTITY = {
    name: "BIZBOOGIE LLC",
    purpose: "Registered legal address · IRS / tax filings only",
    address_line_1: "7925 Lester Dr",
    address_line_2: "Fayetteville, NC 28311",
    address_country: "USA",
    address_oneline: "7925 Lester Dr, Fayetteville, NC 28311, USA",
    address_block: [
        "BIZBOOGIE LLC",
        "7925 Lester Dr",
        "Fayetteville, NC 28311, USA",
    ],
};

export const DIRECTORATE = {
    director: {
        name: "Don Kir",
        title: "Director",
        full_name: "Dontae Kirk",
        email: "director@eigoo.com",
        // Public-facing alias — preferred for property, real estate, IR comms.
        alias_email: "don.kir@bizboogie.com",
        // Sales hub funnel — alias routes here once DNS is wired.
        sales_hub_email: "sales@bizboogie.com",
        // Coordinate of record — synced to invoices, NDA stationery, IR briefs.
        coordinate: LEGAL_ENTITY.address_oneline,
    },
    // Sub-signing identities (per division)
    spf: {
        division: "Sovereign Property Foundry",
        abbr: "SPF",
        signatory_name: "Don Kir",
        signatory_title: "Director · Sovereign Property Foundry",
        signatory_email: "don.kir@bizboogie.com",
        signatory_coordinate: LEGAL_ENTITY.address_oneline,
        proclamation:
            "Property is the anchor that holds the fleet. Investors don't board passengers — they board flagships.",
    },
};
